// client/src/dnd/components/pixi_parts/CombinedVisionOverlay.js
import React, { useEffect, useRef, useState } from "react";
import * as PIXI from "pixi.js";
import { BlurFilter } from "@pixi/filter-blur";
import { useMap } from "react-leaflet";

const MAX_REDRAW_FPS = 30;
const MIN_REDRAW_INTERVAL_MS = 1000 / MAX_REDRAW_FPS;

export default function CombinedVisionOverlay({
  imageSize = null,
  fogEnabled = true,
  wallPolygons = [],
  tokens = [],
  sightMarkers = [],
  lightMarkers = [],
  gridConfig = {},
  coordTransformer = null,
  zIndex = 700,
  fogOpacity = 1,
  revealRadiusMetres = 80,
  rays = 256,
  blurPx = 10,
  overlayDarken = 0.5,
  overlayAssets = [],
  onMaskReady = null,
  mapId = null,
  colour = null,
  azimuth_deg = 0,
  elevation_deg = 45,
  elevation_text_deg = null,
  intensity = null,
  shadowStrength = 0.6,
  shadowSoftness = 0,
  shadowLengthMultiplier = 1.0,
  zeroDegreeShadowMetres = 15,
  reach = 3000,
  reach_units = "metres",
  shadowRTScale = 0.5,
  debug = false,
  onFogAlphaChange = null,
  glowEventName = "fx-glow",
  darksightGreyscale = true,
  darksightGreyFactor = 0.65,
  freezeDuringResize = false,
}) {
  const holderRef = useRef(null);
  const appRef = useRef(null);
  const rootRef = useRef(null);
  const rafIdRef = useRef(null);

  const gradientTexRef = useRef(null);
  const texturesRef = useRef({});

  const ambientLightSpriteRef = useRef(null);
  const fogSpriteRef = useRef(null);
  const darknessSpriteRef = useRef(null);
  const shadowCompositeSpriteRef = useRef(null);
  const overlaysContainerRef = useRef(null);

  const shadowStageRef = useRef(null);
  const shadowGraphicsRef = useRef(null);
  const dirMeshContainerRef = useRef(null);
  const dirGradShaderRef = useRef(null);

  const visibleMaskSpriteRef = useRef(null);
  const unrevealedMaskSpriteRef = useRef(null);

  const shadowRTRef = useRef(null);
  const shadowBlurRTRef = useRef(null);
  const shadowMinusLightsRTRef = useRef(null);
  const visibleMaskRTRef = useRef(null);
  const softVisibleMaskRTRef = useRef(null);
  const unrevealedMaskRTRef = useRef(null);
  const darknessRTRef = useRef(null);

  const dirBlurFilterRef = useRef(null);
  const dirBlurSpriteRef = useRef(null);
  const dirBlurContainerRef = useRef(null);

  const shadowCompositeFilterRef = useRef(null);
  const visibleMaskFilterRef = useRef(null);
  const visibleMaskFilterAmbientRef = useRef(null);
  const visibleMaskFilterDarknessRef = useRef(null);
  const unrevealedMaskFilterOverlayRef = useRef(null);
  const unrevealedCombineFilterRef = useRef(null);

  const wallsRef = useRef(Array.isArray(wallPolygons) ? wallPolygons : []);
  const sightsRef = useRef(Array.isArray(sightMarkers) ? sightMarkers : []);
  const lightsPropsRef = useRef(Array.isArray(lightMarkers) ? lightMarkers : []);
  const gridRef = useRef(gridConfig || {});
  const overlayAssetsRef = useRef(Array.isArray(overlayAssets) ? overlayAssets : []);
  const fogEnabledRef = useRef(!!fogEnabled);
  const imageSizeRef = useRef(imageSize);
  const fogOpacityRef = useRef(clamp01(fogOpacity));
  const revealRef = useRef(revealRadiusMetres);
  const raysRef = useRef(rays);
  const blurPxRef = useRef(blurPx);
  const overlayDarkenRef = useRef(overlayDarken);
  const freezeDuringResizeRef = useRef(!!freezeDuringResize);

  const simpleLightsRef = useRef([]);
  const dbLightsRef = useRef([]);
  const glowPulsesRef = useRef([]);

  const lightIntensityRef = useRef(
    typeof intensity === "number" ? clamp01(intensity) : 1
  );
  const shadowIntensityRef = useRef(clamp01(shadowStrength));
  const lengthMulRef = useRef(shadowLengthMultiplier);
  const shadowScaleRef = useRef(shadowRTScale);
  const softnessRef = useRef(clamp01(shadowSoftness));

  const dsGreyscaleRef = useRef(!!darksightGreyscale);
  const dsGreyFactorRef = useRef(clamp01(darksightGreyFactor));

  const azimuthRef = useRef(azimuth_deg);
  const elevationRef = useRef(elevation_deg);
  const elevationTextDegRef = useRef(parseDeg(elevation_text_deg));
  const reachRef = useRef(reach);
  const reachUnitsRef = useRef(reach_units);
  const fogAlphaRef = useRef(0);
  const ambientLightColorRef = useRef(hexToRgbInt(colour || "#ffe072"));

  const debugLayerRef = useRef(null);
  const debugGRef = useRef(null);
  const debugTextLayerRef = useRef(null);

  const lastUpdateRef = useRef(0);
  const needsRedrawRef = useRef(true);
  const pendingRTResizeRef = useRef(true);
  const rtScreenSizeRef = useRef({ w: 0, h: 0 });
  const resizeIdleTimerRef = useRef(null);
  const resizeActiveRef = useRef(false);
  const forceBurstUntilRef = useRef(0);

  const wallCacheRef = useRef({
    wallsSource: null,
    transformKey: "",
    wallsScreen: [],
    segments: [],
    shadowCasterSegmentsKey: "",
    shadowCasterSegments: [],
  });

  const [dbAzimuth, setDbAzimuth] = useState(null);
  const [dbElevation, setDbElevation] = useState(null);
  const [dbIntensity, setDbIntensity] = useState(null);
  const [dbShadowIntensity, setDbShadowIntensity] = useState(null);
  const [dbSoftness, setDbSoftness] = useState(null);
  const [dbReach, setDbReach] = useState(null);
  const [dbReachUnits, setDbReachUnits] = useState(null);
  const [dbColour, setDbColour] = useState(null);
  const [dbFogAlpha, setDbFogAlpha] = useState(0);

  const map = useMap();

  function markNeedsRedraw() {
    needsRedrawRef.current = true;
  }

  function forceRedrawBurst(ms = 600) {
    forceBurstUntilRef.current = performance.now() + ms;
    markNeedsRedraw();
  }

  function beginResizeBurst() {
    resizeActiveRef.current = true;
    forceRedrawBurst(1000);
    if (resizeIdleTimerRef.current) {
      clearTimeout(resizeIdleTimerRef.current);
      resizeIdleTimerRef.current = null;
    }
    resizeIdleTimerRef.current = setTimeout(() => {
      resizeActiveRef.current = false;
      forceRedrawBurst(900);
    }, 140);
  }

  function replaceRT(ref, w, h) {
    try {
      ref.current?.destroy(true);
    } catch {}
    ref.current = PIXI.RenderTexture.create({
      width: Math.max(2, w | 0),
      height: Math.max(2, h | 0),
      resolution: 1,
    });
  }

  function worldToScreenPx(imagePx) {
    return imagePxToScreenPx(map, imagePx);
  }

  function ensureGradientTexture() {
    if (!gradientTexRef.current) gradientTexRef.current = makeWhiteRadialTexture(512);
    return gradientTexRef.current;
  }

  function ensureDirGradientShader() {
    if (dirGradShaderRef.current) return dirGradShaderRef.current;

    const vert = `
precision mediump float;
attribute vec2 aVertexPosition;
attribute float aT;
uniform mat3 translationMatrix;
uniform mat3 projectionMatrix;
varying float vT;
void main() {
  vT = aT;
  vec3 pos = projectionMatrix * translationMatrix * vec3(aVertexPosition, 1.0);
  gl_Position = vec4(pos.xy, 0.0, 1.0);
}
`;
    const frag = `
precision mediump float;
varying float vT;
uniform float uSoftness;
void main() {
  float hard = 0.22 * (1.0 - clamp(uSoftness, 0.0, 1.0));
  float x = clamp((vT - hard) / max(1e-4, 1.0 - hard), 0.0, 1.0);
  float alpha = 1.0 - x;
  alpha = smoothstep(0.0, 1.0, alpha);
  gl_FragColor = vec4(1.0, 1.0, 1.0, alpha);
}
`;
    const program = PIXI.Program.from(vert, frag);
    dirGradShaderRef.current = new PIXI.Shader(program, { uSoftness: 0.5 });
    return dirGradShaderRef.current;
  }

  function recreateAllRTs() {
    const app = appRef.current;
    if (!app) return;
    if (freezeDuringResizeRef.current) return;

    const r = app.renderer;
    const W = Math.max(1, (r.width || 1) / (r.resolution || 1));
    const H = Math.max(1, (r.height || 1) / (r.resolution || 1));
    const scale = clamp01(shadowScaleRef.current);
    const rw = Math.max(2, Math.floor(W * scale));
    const rh = Math.max(2, Math.floor(H * scale));

    replaceRT(shadowRTRef, rw, rh);
    replaceRT(shadowBlurRTRef, rw, rh);
    replaceRT(shadowMinusLightsRTRef, rw, rh);
    replaceRT(visibleMaskRTRef, W, H);
    replaceRT(softVisibleMaskRTRef, W, H);
    replaceRT(unrevealedMaskRTRef, W, H);
    replaceRT(darknessRTRef, W, H);

    rtScreenSizeRef.current = { w: W, h: H };

    if (shadowStageRef.current) {
      const cont = shadowStageRef.current.children[0];
      if (cont) cont.scale.set(rw / W, rh / H);
    }

    if (dirBlurSpriteRef.current) {
      dirBlurSpriteRef.current.texture = shadowRTRef.current || PIXI.Texture.WHITE;
      dirBlurSpriteRef.current.width = rw;
      dirBlurSpriteRef.current.height = rh;
    }

    if (ambientLightSpriteRef.current) {
      ambientLightSpriteRef.current.width = W;
      ambientLightSpriteRef.current.height = H;
    }

    if (fogSpriteRef.current) {
      fogSpriteRef.current.texture = unrevealedMaskRTRef.current || PIXI.Texture.WHITE;
      fogSpriteRef.current.width = W;
      fogSpriteRef.current.height = H;
    }

    if (darknessSpriteRef.current) {
      darknessSpriteRef.current.texture = darknessRTRef.current || PIXI.Texture.WHITE;
      darknessSpriteRef.current.width = W;
      darknessSpriteRef.current.height = H;
    }

    if (shadowCompositeSpriteRef.current) {
      shadowCompositeSpriteRef.current.width = W;
      shadowCompositeSpriteRef.current.height = H;
    }

    if (shadowCompositeFilterRef.current) {
      shadowCompositeFilterRef.current.uniforms.uShadowTex =
        shadowMinusLightsRTRef.current || shadowRTRef.current || PIXI.Texture.WHITE;
    }

    if (visibleMaskSpriteRef.current) {
      visibleMaskSpriteRef.current.texture = unrevealedMaskRTRef.current || PIXI.Texture.WHITE;
      visibleMaskSpriteRef.current.width = W;
      visibleMaskSpriteRef.current.height = H;
    }

    if (unrevealedMaskSpriteRef.current) {
      unrevealedMaskSpriteRef.current.texture = unrevealedMaskRTRef.current || PIXI.Texture.WHITE;
      unrevealedMaskSpriteRef.current.width = W;
      unrevealedMaskSpriteRef.current.height = H;
    }

    if (visibleMaskFilterRef.current) {
      visibleMaskFilterRef.current.uniforms.uMaskTex = softVisibleMaskRTRef.current || PIXI.Texture.WHITE;
    }
    if (visibleMaskFilterAmbientRef.current) {
      visibleMaskFilterAmbientRef.current.uniforms.uMaskTex = softVisibleMaskRTRef.current || PIXI.Texture.WHITE;
    }
    if (visibleMaskFilterDarknessRef.current) {
      visibleMaskFilterDarknessRef.current.uniforms.uMaskTex = softVisibleMaskRTRef.current || PIXI.Texture.WHITE;
    }
    if (unrevealedMaskFilterOverlayRef.current) {
      unrevealedMaskFilterOverlayRef.current.uniforms.uMaskTex = unrevealedMaskRTRef.current || PIXI.Texture.WHITE;
    }
    if (unrevealedCombineFilterRef.current) {
      unrevealedCombineFilterRef.current.uniforms.uHardMask = visibleMaskRTRef.current || PIXI.Texture.WHITE;
      unrevealedCombineFilterRef.current.uniforms.uSoftMask = softVisibleMaskRTRef.current || PIXI.Texture.WHITE;
    }

    pendingRTResizeRef.current = false;
    markNeedsRedraw();
  }

  function ensureSharedGeometry() {
    const walls = wallsRef.current || [];
    const transformKey = getMapTransformKey(map);

    if (
      wallCacheRef.current.wallsSource === walls &&
      wallCacheRef.current.transformKey === transformKey &&
      Array.isArray(wallCacheRef.current.segments)
    ) {
      return wallCacheRef.current;
    }

    const wallsScreen = walls.map((poly) =>
      (poly || []).map((p) => imageToScreenPoint(map, p.x, p.y))
    );
    const segments = buildSegmentsFromPolys(wallsScreen);

    wallCacheRef.current = {
      wallsSource: walls,
      transformKey,
      wallsScreen,
      segments,
      shadowCasterSegmentsKey: "",
      shadowCasterSegments: [],
    };

    return wallCacheRef.current;
  }

  function ensureShadowCasterSegments(lightDir) {
    const shared = ensureSharedGeometry();
    const key = `${shared.transformKey}|${Math.round((lightDir?.x || 0) * 10000)}|${Math.round((lightDir?.y || 0) * 10000)}`;
    if (shared.shadowCasterSegmentsKey === key && Array.isArray(shared.shadowCasterSegments)) {
      return shared.shadowCasterSegments;
    }
    const out = buildShadowCasterSegmentsFromScreenPolys(shared.wallsScreen || [], lightDir);
    shared.shadowCasterSegmentsKey = key;
    shared.shadowCasterSegments = out;
    return out;
  }

  useEffect(() => {
    wallsRef.current = Array.isArray(wallPolygons) ? wallPolygons : [];
    wallCacheRef.current = {
      wallsSource: null,
      transformKey: "",
      wallsScreen: [],
      segments: [],
      shadowCasterSegmentsKey: "",
      shadowCasterSegments: [],
    };
    markNeedsRedraw();
  }, [wallPolygons]);

  useEffect(() => {
    sightsRef.current = Array.isArray(sightMarkers) ? sightMarkers : [];
    markNeedsRedraw();
  }, [sightMarkers]);

  useEffect(() => {
    lightsPropsRef.current = Array.isArray(lightMarkers) ? lightMarkers : [];
    simpleLightsRef.current = Array.isArray(lightMarkers) ? lightMarkers : [];
    markNeedsRedraw();
  }, [lightMarkers]);

  useEffect(() => {
    gridRef.current = gridConfig || {};
    markNeedsRedraw();
  }, [gridConfig]);

  useEffect(() => {
    overlayAssetsRef.current = Array.isArray(overlayAssets) ? overlayAssets : [];
    markNeedsRedraw();
  }, [overlayAssets]);

  useEffect(() => {
    imageSizeRef.current = imageSize;
    markNeedsRedraw();
  }, [imageSize]);

  useEffect(() => {
    fogEnabledRef.current = !!fogEnabled;
    markNeedsRedraw();
  }, [fogEnabled]);

  useEffect(() => {
    fogOpacityRef.current = clamp01(fogOpacity);
    markNeedsRedraw();
  }, [fogOpacity]);

  useEffect(() => {
    revealRef.current = revealRadiusMetres;
    markNeedsRedraw();
  }, [revealRadiusMetres]);

  useEffect(() => {
    raysRef.current = rays;
    markNeedsRedraw();
  }, [rays]);

  useEffect(() => {
    blurPxRef.current = blurPx;
    markNeedsRedraw();
  }, [blurPx]);

  useEffect(() => {
    overlayDarkenRef.current = overlayDarken;
    markNeedsRedraw();
  }, [overlayDarken]);

  useEffect(() => {
    const wasFrozen = freezeDuringResizeRef.current;
    const nextFrozen = !!freezeDuringResize;
    freezeDuringResizeRef.current = nextFrozen;

    if (wasFrozen && !nextFrozen) {
      const app = appRef.current;
      const holder = holderRef.current;
      if (app && holder) {
        try {
          const newW = holder.clientWidth || 1;
          const newH = holder.clientHeight || 1;
          app.renderer.resize(newW, newH);
        } catch {}
      }
      pendingRTResizeRef.current = true;
      forceRedrawBurst(1400);
    }
  }, [freezeDuringResize]);

  useEffect(() => {
    lengthMulRef.current = shadowLengthMultiplier;
    markNeedsRedraw();
  }, [shadowLengthMultiplier]);

  useEffect(() => {
    shadowScaleRef.current = shadowRTScale;
    pendingRTResizeRef.current = true;
    markNeedsRedraw();
  }, [shadowRTScale]);

  useEffect(() => {
    dsGreyscaleRef.current = !!darksightGreyscale;
    markNeedsRedraw();
  }, [darksightGreyscale]);

  useEffect(() => {
    dsGreyFactorRef.current = clamp01(darksightGreyFactor);
    markNeedsRedraw();
  }, [darksightGreyFactor]);

  useEffect(() => {
    const eff =
      typeof dbIntensity === "number"
        ? clamp01(dbIntensity)
        : typeof intensity === "number"
          ? clamp01(intensity)
          : 1;
    lightIntensityRef.current = eff;
    markNeedsRedraw();
  }, [dbIntensity, intensity]);

  useEffect(() => {
    const eff =
      typeof dbShadowIntensity === "number"
        ? clamp01(dbShadowIntensity)
        : clamp01(shadowStrength);

    shadowIntensityRef.current = eff;

    if (shadowCompositeFilterRef.current) {
      shadowCompositeFilterRef.current.uniforms.uShadowStrength = eff;
    }

    markNeedsRedraw();
  }, [dbShadowIntensity, shadowStrength]);

  useEffect(() => {
    const eff =
      typeof dbSoftness === "number" ? clamp01(dbSoftness) : clamp01(shadowSoftness);
    softnessRef.current = eff;
    markNeedsRedraw();
  }, [dbSoftness, shadowSoftness]);

  useEffect(() => {
    const resolved = dbColour || colour || "#ffe072";
    ambientLightColorRef.current = hexToRgbInt(resolved);

    if (ambientLightSpriteRef.current) {
      ambientLightSpriteRef.current.tint = rgbIntToHexInt(ambientLightColorRef.current);
    }
    if (fogSpriteRef.current) {
      fogSpriteRef.current.tint = 0x000000;
    }
    if (darknessSpriteRef.current) {
      darknessSpriteRef.current.tint = 0x000000;
    }
    markNeedsRedraw();
  }, [dbColour, colour]);

  useEffect(() => {
    azimuthRef.current = typeof dbAzimuth === "number" ? dbAzimuth : azimuth_deg;
    elevationRef.current = typeof dbElevation === "number" ? dbElevation : elevation_deg;
    reachRef.current = typeof dbReach === "number" ? dbReach : reach;
    reachUnitsRef.current = dbReachUnits || reach_units;
    markNeedsRedraw();
  }, [dbAzimuth, dbElevation, dbReach, dbReachUnits, azimuth_deg, elevation_deg, reach, reach_units]);

  useEffect(() => {
    elevationTextDegRef.current = parseDeg(elevation_text_deg);
    markNeedsRedraw();
  }, [elevation_text_deg]);

  useEffect(() => {
    fogAlphaRef.current = clamp01(dbFogAlpha);
    if (typeof onFogAlphaChange === "function") {
      try {
        onFogAlphaChange(fogAlphaRef.current);
      } catch {}
    }
    markNeedsRedraw();
  }, [dbFogAlpha, onFogAlphaChange]);

  useEffect(() => {
    const handler = (ev) => {
      const d = ev?.detail || {};
      const pulse = normalizeGlowEventPayload(d, gridRef.current);
      if (pulse) {
        glowPulsesRef.current.push(pulse);
        markNeedsRedraw();
      }
    };

    window.addEventListener(glowEventName, handler);

    if (!window.__emitGlow) {
      try {
        window.__emitGlow = (detail) =>
          window.dispatchEvent(new CustomEvent(glowEventName, { detail }));
      } catch {}
    }

    return () => {
      window.removeEventListener(glowEventName, handler);
    };
  }, [glowEventName]);

  useEffect(() => {
    const resolvedMapId = Number.isFinite(Number(mapId)) ? Number(mapId) : inferMapIdFromPath();

    const applyDetail = (detail) => {
      if (!detail) return;
      const eventMapId = Number(detail.map_id);
      if (resolvedMapId && Number.isFinite(eventMapId) && eventMapId !== resolvedMapId) return;

      if (typeof detail.azimuth_deg === "number") setDbAzimuth(detail.azimuth_deg);
      if (typeof detail.elevation_deg === "number") setDbElevation(detail.elevation_deg);
      if (typeof detail.intensity === "number") setDbIntensity(clamp01(detail.intensity));
      if (typeof detail.shadow_intensity === "number") setDbShadowIntensity(clamp01(detail.shadow_intensity));
      if (typeof detail.softness === "number") setDbSoftness(clamp01(detail.softness));
      if (typeof detail.reach === "number") setDbReach(detail.reach);
      if (typeof detail.reach_units === "string") setDbReachUnits(detail.reach_units);
      if (typeof detail.colour === "string" && detail.colour) setDbColour(detail.colour);
      if (typeof detail.ambient_darkness === "number") setDbFogAlpha(clamp01(detail.ambient_darkness));
    };

    const genericHandler = (ev) => applyDetail(ev?.detail || {});
    const scopedName = resolvedMapId ? `globalLight:update:${resolvedMapId}` : null;
    const scopedHandler = (ev) => applyDetail(ev?.detail || {});

    window.addEventListener("globalLight:update", genericHandler);
    if (scopedName) window.addEventListener(scopedName, scopedHandler);

    return () => {
      window.removeEventListener("globalLight:update", genericHandler);
      if (scopedName) window.removeEventListener(scopedName, scopedHandler);
    };
  }, [mapId]);

  useEffect(() => {
    let alive = true;
    const resolvedMapId = Number.isFinite(Number(mapId)) ? Number(mapId) : inferMapIdFromPath();
    if (!resolvedMapId) return;

    const fetchAll = async () => {
      try {
        const r = await fetch(`/api/tabletop/tokens/${resolvedMapId}`, {
          credentials: "include",
        });
        if (!alive) return;
        if (r?.ok) {
          const arr = await r.json();
          const gl = Array.isArray(arr)
            ? arr.find((t) => t && t.type === "global_light")
            : null;

          if (gl) {
            if (typeof gl.azimuth_deg === "number") setDbAzimuth(gl.azimuth_deg);
            if (typeof gl.elevation_deg === "number") setDbElevation(gl.elevation_deg);
            if (typeof gl.intensity === "number") setDbIntensity(clamp01(gl.intensity));
            if (typeof gl.shadow_intensity === "number") setDbShadowIntensity(clamp01(gl.shadow_intensity));
            if (typeof gl.softness === "number") setDbSoftness(clamp01(gl.softness));
            if (typeof gl.reach === "number") setDbReach(gl.reach);
            if (typeof gl.reach_units === "string") setDbReachUnits(gl.reach_units);
            if (typeof gl.colour === "string" && gl.colour) setDbColour(gl.colour);
          }

          const lights = Array.isArray(arr)
            ? arr
                .filter((t) => t && t.emits_light && t.type !== "global_light")
                .map((t) => ({
                  id: t.id,
                  x: t.x,
                  y: t.y,
                  lat: t.lat,
                  lng: t.lng,
                  reach: t.reach,
                  reach_units: t.reach_units || null,
                  intensity: typeof t.intensity === "number" ? t.intensity : 1,
                  flicker: t.flicker ? 1 : 0,
                  light_type: t.light_type || null,
                  type: t.type || null,
                }))
            : [];

          dbLightsRef.current = lights;
          markNeedsRedraw();
        }
      } catch {}

      try {
        const r2 = await fetch(`/api/tabletop/maps/${resolvedMapId}/lighting`, {
          credentials: "include",
        });
        if (!alive) return;
        if (r2?.ok) {
          const j = await r2.json();
          const g = typeof j.global_light === "number" ? j.global_light : 0;
          setDbFogAlpha(g);
          if (typeof j.shadow_intensity === "number") {
            setDbShadowIntensity(clamp01(j.shadow_intensity));
          }
        }
      } catch {}
    };

    fetchAll();
    const t = setInterval(fetchAll, 1500);

    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [mapId]);

  useEffect(() => {
    const parent = holderRef.current;
    if (!parent) return;

    const W = parent.clientWidth || 1;
    const H = parent.clientHeight || 1;

    const app = new PIXI.Application({
      width: W,
      height: H,
      antialias: false,
      autoDensity: true,
      resolution: Math.min(2, Math.max(1, window.devicePixelRatio || 1)),
      backgroundAlpha: 0,
      powerPreference: "high-performance",
    });

    parent.appendChild(app.view);
    appRef.current = app;

    const root = new PIXI.Container();
    app.stage.addChild(root);
    rootRef.current = root;

    const fogSprite = new PIXI.Sprite(PIXI.Texture.WHITE);
    fogSprite.width = W;
    fogSprite.height = H;
    fogSprite.tint = 0x000000;
    root.addChild(fogSprite);
    fogSpriteRef.current = fogSprite;

    const overlaysContainer = new PIXI.Container();
    root.addChild(overlaysContainer);
    overlaysContainerRef.current = overlaysContainer;

    const ambientSprite = new PIXI.Sprite(PIXI.Texture.WHITE);
    ambientSprite.width = W;
    ambientSprite.height = H;
    ambientSprite.tint = rgbIntToHexInt(ambientLightColorRef.current);
    ambientSprite.blendMode = PIXI.BLEND_MODES.SCREEN;
    ambientSprite.alpha = 0;
    root.addChild(ambientSprite);
    ambientLightSpriteRef.current = ambientSprite;

    const shadowCompositeSprite = new PIXI.Sprite(PIXI.Texture.WHITE);
    shadowCompositeSprite.width = W;
    shadowCompositeSprite.height = H;
    root.addChild(shadowCompositeSprite);
    shadowCompositeSpriteRef.current = shadowCompositeSprite;

    const darknessSprite = new PIXI.Sprite(PIXI.Texture.WHITE);
    darknessSprite.width = W;
    darknessSprite.height = H;
    darknessSprite.tint = 0x000000;
    root.addChild(darknessSprite);
    darknessSpriteRef.current = darknessSprite;

    const shadowStage = new PIXI.Container();
    const scaled = new PIXI.Container();
    shadowStage.addChild(scaled);

    const shadowG = new PIXI.Graphics();
    scaled.addChild(shadowG);

    const gradMeshContainer = new PIXI.Container();
    scaled.addChild(gradMeshContainer);

    shadowStageRef.current = shadowStage;
    shadowGraphicsRef.current = shadowG;
    dirMeshContainerRef.current = gradMeshContainer;

    const dirSprite = new PIXI.Sprite(PIXI.Texture.WHITE);
    const dirBlur = new BlurFilter();
    dirBlur.quality = 1;
    dirBlur.resolution = 5;
    dirBlurFilterRef.current = dirBlur;
    dirSprite.filters = [dirBlur];
    const dirContainer = new PIXI.Container();
    dirContainer.addChild(dirSprite);
    dirBlurSpriteRef.current = dirSprite;
    dirBlurContainerRef.current = dirContainer;

    const visibleMaskSprite = new PIXI.Sprite(PIXI.Texture.WHITE);
    visibleMaskSprite.position.set(0, 0);
    visibleMaskSprite.width = W;
    visibleMaskSprite.height = H;
    visibleMaskSpriteRef.current = visibleMaskSprite;

    const unrevealedMaskSprite = new PIXI.Sprite(PIXI.Texture.WHITE);
    unrevealedMaskSprite.position.set(0, 0);
    unrevealedMaskSprite.width = W;
    unrevealedMaskSprite.height = H;
    unrevealedMaskSpriteRef.current = unrevealedMaskSprite;

    const shadowFrag = `
precision mediump float;
varying vec2 vTextureCoord;
uniform sampler2D uShadowTex;
uniform float uShadowStrength;
uniform vec3 uShadowColor;
void main(){
  float m = texture2D(uShadowTex, vTextureCoord).a;
  float a = clamp(m * uShadowStrength, 0.0, 1.0);
  gl_FragColor = vec4(uShadowColor.rgb, a);
}
`;
    const compFilter = new PIXI.Filter(undefined, shadowFrag, {
      uShadowTex: PIXI.Texture.WHITE,
      uShadowStrength: shadowIntensityRef.current,
      uShadowColor: [0, 0, 0],
    });
    shadowCompositeFilterRef.current = compFilter;

    const maskAlphaFilter = new MaskAlphaMultiplyFilter(PIXI.Texture.WHITE);
    const maskAlphaAmbient = new MaskAlphaMultiplyFilter(PIXI.Texture.WHITE);
    const maskAlphaDarkness = new MaskAlphaMultiplyFilter(PIXI.Texture.WHITE);
    const overlayMaskAlpha = new MaskAlphaMultiplyFilter(PIXI.Texture.WHITE);

    visibleMaskFilterRef.current = maskAlphaFilter;
    visibleMaskFilterAmbientRef.current = maskAlphaAmbient;
    visibleMaskFilterDarknessRef.current = maskAlphaDarkness;
    unrevealedMaskFilterOverlayRef.current = overlayMaskAlpha;
    unrevealedCombineFilterRef.current = new UnrevealedFogCombineFilter(
      PIXI.Texture.WHITE,
      PIXI.Texture.WHITE
    );

    shadowCompositeSprite.filters = [compFilter, maskAlphaFilter];
    ambientSprite.filters = [maskAlphaAmbient];
    // Keep fog-of-war / LOS out of the darkness pass.
    // Darkness is part of the underlying lighting result and should remain
    // continuous across the whole map; the fog layer hides or reveals that
    // finished lighting result afterwards.
    darknessSprite.filters = null;
    overlaysContainer.filters = [overlayMaskAlpha];

    if (debug) {
      const dbgLayer = new PIXI.Container();
      const dbgG = new PIXI.Graphics();
      const dbgTexts = new PIXI.Container();
      dbgLayer.addChild(dbgG);
      dbgLayer.addChild(dbgTexts);
      root.addChild(dbgLayer);
      debugLayerRef.current = dbgLayer;
      debugGRef.current = dbgG;
      debugTextLayerRef.current = dbgTexts;
    }

    if (!gradientTexRef.current) {
      gradientTexRef.current = makeWhiteRadialTexture(512);
    }

    pendingRTResizeRef.current = true;
    forceRedrawBurst(1200);

    const onMapVisualChange = () => {
      if (freezeDuringResizeRef.current) return;
      wallCacheRef.current.transformKey = "";
      forceRedrawBurst(350);
    };

    const onResize = () => {
      if (freezeDuringResizeRef.current) return;
      if (!holderRef.current || !appRef.current) return;
      const newW = holderRef.current.clientWidth || 1;
      const newH = holderRef.current.clientHeight || 1;

      try {
        appRef.current.renderer.resize(newW, newH);
      } catch {}

      if (fogSpriteRef.current) {
        fogSpriteRef.current.width = newW;
        fogSpriteRef.current.height = newH;
      }
      if (ambientLightSpriteRef.current) {
        ambientLightSpriteRef.current.width = newW;
        ambientLightSpriteRef.current.height = newH;
      }
      if (shadowCompositeSpriteRef.current) {
        shadowCompositeSpriteRef.current.width = newW;
        shadowCompositeSpriteRef.current.height = newH;
      }
      if (darknessSpriteRef.current) {
        darknessSpriteRef.current.width = newW;
        darknessSpriteRef.current.height = newH;
      }
      if (visibleMaskSpriteRef.current) {
        visibleMaskSpriteRef.current.width = newW;
        visibleMaskSpriteRef.current.height = newH;
      }
      if (unrevealedMaskSpriteRef.current) {
        unrevealedMaskSpriteRef.current.width = newW;
        unrevealedMaskSpriteRef.current.height = newH;
      }

      pendingRTResizeRef.current = true;
      wallCacheRef.current.transformKey = "";
      beginResizeBurst();
    };

    const ro = new ResizeObserver(onResize);
    ro.observe(parent);

    map.on("movestart", onMapVisualChange);
    map.on("move", onMapVisualChange);
    map.on("moveend", onMapVisualChange);
    map.on("zoomstart", onMapVisualChange);
    map.on("zoom", onMapVisualChange);
    map.on("zoomend", onMapVisualChange);
    map.on("resize", onMapVisualChange);
    map.on("viewreset", onMapVisualChange);

    const loop = () => {
      drawFrame();
      rafIdRef.current = requestAnimationFrame(loop);
    };
    rafIdRef.current = requestAnimationFrame(loop);

    return () => {
      ro.disconnect();

      map.off("movestart", onMapVisualChange);
      map.off("move", onMapVisualChange);
      map.off("moveend", onMapVisualChange);
      map.off("zoomstart", onMapVisualChange);
      map.off("zoom", onMapVisualChange);
      map.off("zoomend", onMapVisualChange);
      map.off("resize", onMapVisualChange);
      map.off("viewreset", onMapVisualChange);

      if (resizeIdleTimerRef.current) {
        clearTimeout(resizeIdleTimerRef.current);
        resizeIdleTimerRef.current = null;
      }

      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }

      destroyRT(shadowRTRef);
      destroyRT(shadowBlurRTRef);
      destroyRT(shadowMinusLightsRTRef);
      destroyRT(visibleMaskRTRef);
      destroyRT(softVisibleMaskRTRef);
      destroyRT(unrevealedMaskRTRef);
      destroyRT(darknessRTRef);

      try {
        const cache = texturesRef.current || {};
        Object.values(cache).forEach((tex) => {
          try {
            tex.destroy(true);
          } catch {}
        });
      } catch {}
      texturesRef.current = {};

      try {
        app.destroy(true, { children: true });
      } catch {}

      appRef.current = null;
      rootRef.current = null;
    };
  }, [debug, map]);

  function drawFrame() {
    const app = appRef.current;
    if (!app || !map) return;
    if (!imageSizeRef.current) return;
    if (freezeDuringResizeRef.current) return;

    const nowMs = performance.now();
    const inBurst = nowMs < forceBurstUntilRef.current;
    const hasActiveFlicker = hasEnabledFlicker(
      dbLightsRef.current,
      lightsPropsRef.current
    );
    if (hasActiveFlicker) {
      needsRedrawRef.current = true;
    }

    const activeGlowPulses = glowPulsesRef.current;

    if (activeGlowPulses.length > 0) {
      let hasActivePulse = false;
      const nowS = nowMs / 1000;
      for (let i = 0; i < activeGlowPulses.length; i++) {
        const p = activeGlowPulses[i];
        if (p && nowS - p.t0 < Math.max(0.0001, p.durationS)) {
          hasActivePulse = true;
          break;
        }
      }
      if (hasActivePulse) {
        needsRedrawRef.current = true;
      }
    }

    if (!needsRedrawRef.current && !pendingRTResizeRef.current && !inBurst) {
      return;
    }

    const last = lastUpdateRef.current || 0;
    if (nowMs - last < MIN_REDRAW_INTERVAL_MS) {
      return;
    }

    const r = app.renderer;
    const W = Math.max(1, (r.width || 1) / (r.resolution || 1));
    const H = Math.max(1, (r.height || 1) / (r.resolution || 1));

    const prevSize = rtScreenSizeRef.current || { w: 0, h: 0 };
    if (pendingRTResizeRef.current || prevSize.w !== W || prevSize.h !== H) {
      recreateAllRTs();
    }

    if (
      !shadowRTRef.current ||
      !shadowMinusLightsRTRef.current ||
      !visibleMaskRTRef.current ||
      !softVisibleMaskRTRef.current ||
      !unrevealedMaskRTRef.current ||
      !darknessRTRef.current
    ) {
      return;
    }

    needsRedrawRef.current = false;
    lastUpdateRef.current = nowMs;

    const shared = ensureSharedGeometry();
    const segments = shared.segments || [];

    const lightDir = azimuthToShadowVector(azimuthRef.current);

    const typedDeg = elevationTextDegRef.current;
    let uiDeg;
    if (typeof typedDeg === "number" && isFinite(typedDeg)) {
      uiDeg = clamp(typedDeg, 0, 90);
    } else {
      const rawElev = Number(elevationRef.current);
      if (Number.isFinite(rawElev)) {
        if (rawElev >= 0 && rawElev <= 1) {
          uiDeg = clamp(rawElev * 90, 0, 90);
        } else {
          uiDeg = clamp(rawElev, 0, 90);
        }
      } else {
        uiDeg = 90;
      }
    }

    const metresInPixels =
      Number(gridRef.current?.metre_in_pixels ?? gridRef.current?.meter_in_pixels) || 0;
    const maxShadowMetres = Math.max(0, Number(zeroDegreeShadowMetres) || 0);
    const shadowMetres = maxShadowMetres * (1 - uiDeg / 90);
    const shadowImagePx = shadowMetres * metresInPixels;
    const L =
      metresInPixels > 0
        ? worldToScreenPx(shadowImagePx) * clamp(lengthMulRef.current || 1.0, 0, 10)
        : 0;

    const lightIntensity = clamp01(lightIntensityRef.current);
    const ambientDarknessAlpha = clamp01(fogAlphaRef.current);
    const shadowStrengthNow = clamp01(shadowIntensityRef.current);

    if (shadowCompositeFilterRef.current) {
      shadowCompositeFilterRef.current.uniforms.uShadowStrength = shadowStrengthNow;
      shadowCompositeFilterRef.current.uniforms.uShadowTex =
        shadowMinusLightsRTRef.current || shadowRTRef.current || PIXI.Texture.WHITE;
    }

    if (visibleMaskFilterRef.current) {
      visibleMaskFilterRef.current.uniforms.uMaskTex = softVisibleMaskRTRef.current || PIXI.Texture.WHITE;
    }
    if (visibleMaskFilterAmbientRef.current) {
      visibleMaskFilterAmbientRef.current.uniforms.uMaskTex = softVisibleMaskRTRef.current || PIXI.Texture.WHITE;
    }
    if (visibleMaskFilterDarknessRef.current) {
      visibleMaskFilterDarknessRef.current.uniforms.uMaskTex = softVisibleMaskRTRef.current || PIXI.Texture.WHITE;
    }
    if (unrevealedMaskFilterOverlayRef.current) {
      unrevealedMaskFilterOverlayRef.current.uniforms.uMaskTex = unrevealedMaskRTRef.current || PIXI.Texture.WHITE;
    }
    if (unrevealedCombineFilterRef.current) {
      unrevealedCombineFilterRef.current.uniforms.uHardMask = visibleMaskRTRef.current || PIXI.Texture.WHITE;
      unrevealedCombineFilterRef.current.uniforms.uSoftMask = softVisibleMaskRTRef.current || PIXI.Texture.WHITE;
    }

    if (ambientLightSpriteRef.current) {
      const tintAlpha = clamp01(((1 - ambientDarknessAlpha) * 0.22 + 0.06) * lightIntensity);
      ambientLightSpriteRef.current.tint = rgbIntToHexInt(ambientLightColorRef.current);
      ambientLightSpriteRef.current.alpha = tintAlpha;
    }

    const list = mergeLights(
      dbLightsRef.current || [],
      lightsPropsRef.current || []
    ).filter(isRenderableLight);

    const grad = ensureGradientTexture();
    const texW = grad?.baseTexture?.width || 512;

    {
      const arr = glowPulsesRef.current;
      let w = 0;
      const nowS = nowMs / 1000;
      for (let i = 0; i < arr.length; i++) {
        const p = arr[i];
        const t = (nowS - p.t0) / Math.max(0.0001, p.durationS);
        if (t < 1) {
          arr[w++] = p;
        }
      }
      arr.length = w;
      if (arr.length > 0) {
        markNeedsRedraw();
      }
    }

    const effectiveRays = Math.max(16, Math.floor(raysRef.current || 256));
    const revealImagePx = getRevealRadiusImagePx(gridRef.current, revealRef.current);

    const sightVisibilityPolys = [];
    const sights = sightsRef.current || [];
    for (let i = 0; i < sights.length; i++) {
      const sight = sights[i];
      const center = markerToScreen(map, sight);
      if (!center || !isFinite(center.x) || !isFinite(center.y)) continue;

      let rScreenPx = worldToScreenPx(revealImagePx);
      if (!isFinite(rScreenPx) || rScreenPx <= 0.5) rScreenPx = 100;

      const poly = computeVisibilityPolygonByIntersect(
        center,
        segments,
        Math.max(1, rScreenPx),
        effectiveRays
      );
      sightVisibilityPolys.push({
        marker: sight,
        center,
        rScreenPx,
        poly,
      });
    }

    renderVisibleAndUnrevealedMasks({
      renderer: r,
      W,
      H,
      grad,
      texW,
      sightVisibilityPolys,
    });

    renderDirectionalShadows({
      renderer: r,
      W,
      H,
      lightDir,
      L,
      uiDeg,
    });

    renderDarknessWithLocalCutouts({
      renderer: r,
      W,
      H,
      grad,
      texW,
      list,
      segments,
      nowS: nowMs / 1000,
    });

    renderOverlayAssets({
      W,
      H,
    });

    if (fogSpriteRef.current) {
      fogSpriteRef.current.texture = unrevealedMaskRTRef.current || PIXI.Texture.WHITE;
      fogSpriteRef.current.tint = 0x000000;
      fogSpriteRef.current.alpha = fogEnabledRef.current ? fogOpacityRef.current : 0;
    }

    if (darknessSpriteRef.current) {
      darknessSpriteRef.current.texture = darknessRTRef.current || PIXI.Texture.WHITE;
      darknessSpriteRef.current.tint = 0x000000;
      darknessSpriteRef.current.alpha = 1;
    }

    if (visibleMaskSpriteRef.current) {
      visibleMaskSpriteRef.current.texture = unrevealedMaskRTRef.current || PIXI.Texture.WHITE;
    }
    if (unrevealedMaskSpriteRef.current) {
      unrevealedMaskSpriteRef.current.texture = unrevealedMaskRTRef.current || PIXI.Texture.WHITE;
      try {
        if (typeof onMaskReady === "function") onMaskReady(unrevealedMaskSpriteRef.current);
      } catch {}
    }

    if (debug && debugLayerRef.current && debugGRef.current) {
      const dbg = debugGRef.current;
      const texts = debugTextLayerRef.current;
      dbg.clear();

      if (texts) {
        try {
          const kids = texts.removeChildren();
          for (let i = 0; i < kids.length; i++) {
            try {
              kids[i].destroy(true);
            } catch {}
          }
        } catch {}
      }

      dbg.lineStyle(3, 0x00ffff, 1);
      const ax = 40;
      const ay = 40;
      dbg.moveTo(ax, ay);
      dbg.lineTo(ax + lightDir.x * 60, ay + lightDir.y * 60);
      dbg.beginFill(0x00ffff, 1);
      dbg.drawCircle(ax + lightDir.x * 60, ay + lightDir.y * 60, 3);
      dbg.endFill();

      dbg.lineStyle(2, 0x00ff00, 1);
      const wallsDbg = shared.wallsScreen || [];
      for (let p = 0; p < wallsDbg.length; p++) {
        const poly = wallsDbg[p];
        if (!poly || poly.length < 2) continue;
        for (let i = 0; i < poly.length; i++) {
          const A = poly[i];
          const B = poly[(i + 1) % poly.length];
          dbg.moveTo(A.x, A.y);
          dbg.lineTo(B.x, B.y);
        }
      }

      dbg.lineStyle(2, 0xff00ff, 0.8);
      for (let i = 0; i < sightVisibilityPolys.length; i++) {
        const entry = sightVisibilityPolys[i];
        const poly = entry.poly;
        if (!poly || poly.length < 3) continue;
        dbg.moveTo(poly[0].x, poly[0].y);
        for (let j = 1; j < poly.length; j++) dbg.lineTo(poly[j].x, poly[j].y);
        dbg.lineTo(poly[0].x, poly[0].y);
      }
    }
  }

  function renderVisibleAndUnrevealedMasks({
    renderer,
    W,
    H,
    grad,
    texW,
    sightVisibilityPolys,
  }) {
    const hardVisibleContainer = new PIXI.Container();

    for (let i = 0; i < sightVisibilityPolys.length; i++) {
      const entry = sightVisibilityPolys[i];
      const { poly } = entry;
      if (!poly || poly.length < 3) continue;

      const g = new PIXI.Graphics();
      g.beginFill(0xffffff, 1);
      g.moveTo(poly[0].x, poly[0].y);
      for (let j = 1; j < poly.length; j++) g.lineTo(poly[j].x, poly[j].y);
      g.closePath();
      g.endFill();
      hardVisibleContainer.addChild(g);
    }

    renderer.render(hardVisibleContainer, {
      renderTexture: visibleMaskRTRef.current,
      clear: true,
    });

    try {
      hardVisibleContainer.destroy({ children: true });
    } catch {}

    const softVisibleContainer = new PIXI.Container();

    for (let i = 0; i < sightVisibilityPolys.length; i++) {
      const entry = sightVisibilityPolys[i];
      const { poly } = entry;
      if (!poly || poly.length < 3) continue;

      const softPoly = new PIXI.Graphics();
      softPoly.beginFill(0xffffff, 1);
      softPoly.moveTo(poly[0].x, poly[0].y);
      for (let j = 1; j < poly.length; j++) softPoly.lineTo(poly[j].x, poly[j].y);
      softPoly.closePath();
      softPoly.endFill();
      softVisibleContainer.addChild(softPoly);
    }

    const fogFeather = Math.max(0, Number(blurPxRef.current) || 0);
    if (fogFeather > 0.25) {
      const blur = new BlurFilter();
      blur.blur = fogFeather;
      blur.quality = 1;
      blur.resolution = 1;
      blur.padding = Math.ceil(fogFeather * 2);
      softVisibleContainer.filters = [blur];
    }

    renderer.render(softVisibleContainer, {
      renderTexture: softVisibleMaskRTRef.current,
      clear: true,
    });

    try {
      softVisibleContainer.destroy({ children: true });
    } catch {}

    const combineSprite = new PIXI.Sprite(PIXI.Texture.WHITE);
    combineSprite.width = W;
    combineSprite.height = H;
    combineSprite.filters = [unrevealedCombineFilterRef.current];

    renderer.render(combineSprite, {
      renderTexture: unrevealedMaskRTRef.current,
      clear: true,
    });

    try {
      combineSprite.destroy(true);
    } catch {}
  }

  function renderDirectionalShadows({
    renderer,
    W,
    H,
    lightDir,
    L,
    uiDeg,
  }) {
    const g = shadowGraphicsRef.current;
    if (g) g.clear();

    const meshLayer = dirMeshContainerRef.current;
    if (meshLayer) {
      try {
        const kids = meshLayer.removeChildren();
        for (let i = 0; i < kids.length; i++) {
          try {
            kids[i].destroy(true);
          } catch {}
        }
      } catch {}
    }

    const shader = ensureDirGradientShader();
    const softnessMix = clamp01(
      softnessRef.current * 0.7 + (uiDeg / 90) * 0.3
    );
    shader.uniforms.uSoftness = softnessMix;

    const shadowCasterSegments = ensureShadowCasterSegments(lightDir);

    if (L > 0.0001 && meshLayer) {
      for (let i = 0; i < shadowCasterSegments.length; i++) {
        const seg = shadowCasterSegments[i];
        const A = seg.a;
        const B = seg.b;
        const segStrength = clamp01(seg.facing);
        const segLength = L * (0.35 + segStrength * 0.65);
        const A2 = { x: A.x + lightDir.x * segLength, y: A.y + lightDir.y * segLength };
        const B2 = { x: B.x + lightDir.x * segLength, y: B.y + lightDir.y * segLength };

        const geom = new PIXI.Geometry()
          .addAttribute(
            "aVertexPosition",
            new Float32Array([A.x, A.y, B.x, B.y, B2.x, B2.y, A2.x, A2.y]),
            2
          )
          .addAttribute("aT", new Float32Array([0, 0, 1, 1]), 1)
          .addIndex([0, 1, 2, 0, 2, 3]);

        const mesh = new PIXI.Mesh(geom, shader);
        mesh.alpha = 0.4 + segStrength * 0.6;
        meshLayer.addChild(mesh);
      }
    }

    renderer.render(shadowStageRef.current, {
      renderTexture: shadowRTRef.current,
      clear: true,
    });

    const soft = clamp01(softnessMix);
    if (soft > 0.0001) {
      const maxScreenPx = 30;
      const scale = clamp01(shadowScaleRef.current);
      const blurPxRT = maxScreenPx * scale * soft;
      const bf = dirBlurFilterRef.current;
      bf.blur = blurPxRT;
      bf.padding = Math.ceil(bf.blur * 2);
      renderer.render(dirBlurContainerRef.current, {
        renderTexture: shadowBlurRTRef.current,
        clear: true,
      });
    } else {
      const passthrough = new PIXI.Sprite(shadowRTRef.current || PIXI.Texture.WHITE);
      passthrough.width = shadowBlurRTRef.current?.width || 2;
      passthrough.height = shadowBlurRTRef.current?.height || 2;
      renderer.render(passthrough, {
        renderTexture: shadowBlurRTRef.current,
        clear: true,
      });
      try {
        passthrough.destroy(true);
      } catch {}
    }
  }

  function renderDarknessWithLocalCutouts({
    renderer,
    W,
    H,
    grad,
    texW,
    list,
    segments,
    nowS,
  }) {
    const root = new PIXI.Container();
    const scaledCont = new PIXI.Container();
    const rw =
      shadowMinusLightsRTRef.current?.width ||
      Math.max(2, Math.floor(W * clamp01(shadowScaleRef.current)));
    const rh =
      shadowMinusLightsRTRef.current?.height ||
      Math.max(2, Math.floor(H * clamp01(shadowScaleRef.current)));
    scaledCont.scale.set(rw / W, rh / H);
    root.addChild(scaledCont);

    const srcShadowTex =
      clamp01(softnessRef.current * 0.7 + 0.0001) > 0.0001
        ? shadowBlurRTRef.current
        : shadowRTRef.current;

    const baseShadow = new PIXI.Sprite(srcShadowTex || PIXI.Texture.WHITE);
    baseShadow.width = W;
    baseShadow.height = H;
    scaledCont.addChild(baseShadow);

    const darknessCont = new PIXI.Container();
    const fogRect = new PIXI.Graphics();
    fogRect.beginFill(0xffffff, clamp01(fogAlphaRef.current));
    fogRect.drawRect(0, 0, W, H);
    fogRect.endFill();
    darknessCont.addChild(fogRect);

    const dsErase = dsGreyscaleRef.current ? 1 - dsGreyFactorRef.current : 1;

    for (let i = 0; i < list.length; i++) {
      const lm = list[i];
      const center = markerToScreen(map, lm);
      if (!center || !isFinite(center.x) || !isFinite(center.y)) continue;

      const rImagePx = lightImageRadiusPx(lm, gridRef.current);
      let rScreenPx = worldToScreenPx(rImagePx);
      if (!isFinite(rScreenPx) || rScreenPx <= 0.5) continue;

      const polyPts = computeVisibilityPolygonByIntersect(
        center,
        segments,
        Math.max(1, rScreenPx),
        192
      );

      let maskGScaled = null;
      let maskGDarkness = null;

      if (polyPts && polyPts.length >= 3) {
        maskGScaled = new PIXI.Graphics();
        maskGScaled.beginFill(0xffffff, 1);
        maskGScaled.moveTo(polyPts[0].x, polyPts[0].y);
        for (let j = 1; j < polyPts.length; j++) maskGScaled.lineTo(polyPts[j].x, polyPts[j].y);
        maskGScaled.closePath();
        maskGScaled.endFill();
        scaledCont.addChild(maskGScaled);

        maskGDarkness = new PIXI.Graphics();
        maskGDarkness.beginFill(0xffffff, 1);
        maskGDarkness.moveTo(polyPts[0].x, polyPts[0].y);
        for (let j = 1; j < polyPts.length; j++) maskGDarkness.lineTo(polyPts[j].x, polyPts[j].y);
        maskGDarkness.closePath();
        maskGDarkness.endFill();
        darknessCont.addChild(maskGDarkness);
      }

      const baseAlpha = clamp01(typeof lm.intensity === "number" ? lm.intensity : 1);
      const flickerAmt = Number(lm.flicker || 0);
      const seed = lightSeed(lm);
      const flicker = flickerAmt ? candleFlicker(nowS, seed) : NO_FLICKER;
      const alphaJitter = 1 + flicker.brightness * 0.07 * flickerAmt;
      const scaleJitter = 1 + flicker.radius * 0.012 * flickerAmt;

      if (isDarkSight(lm)) {
        const sq = oneSquareScreenPx(map, gridRef.current);
        const minR = sq * 0.5;
        if (rScreenPx < minR) rScreenPx = minR;

        const innerR = minR;

        const hardShadow = new PIXI.Graphics();
        hardShadow.beginFill(0xffffff, 1);
        hardShadow.drawCircle(center.x, center.y, innerR);
        hardShadow.endFill();
        hardShadow.alpha = clamp01(dsErase);
        hardShadow.blendMode = PIXI.BLEND_MODES.ERASE;
        if (maskGScaled) hardShadow.mask = maskGScaled;
        scaledCont.addChild(hardShadow);

        const hardDarkness = new PIXI.Graphics();
        hardDarkness.beginFill(0xffffff, 1);
        hardDarkness.drawCircle(center.x, center.y, innerR);
        hardDarkness.endFill();
        hardDarkness.alpha = clamp01(dsErase);
        hardDarkness.blendMode = PIXI.BLEND_MODES.ERASE;
        if (maskGDarkness) hardDarkness.mask = maskGDarkness;
        darknessCont.addChild(hardDarkness);

        const spShadow = new PIXI.Sprite(grad);
        spShadow.anchor.set(0.5);
        spShadow.position.set(center.x, center.y);
        spShadow.scale.set((rScreenPx * 2) / texW);
        spShadow.alpha = clamp01(0.6 * dsErase);
        spShadow.blendMode = PIXI.BLEND_MODES.ERASE;
        if (maskGScaled) spShadow.mask = maskGScaled;
        scaledCont.addChild(spShadow);

        const spDarkness = new PIXI.Sprite(grad);
        spDarkness.anchor.set(0.5);
        spDarkness.position.set(center.x, center.y);
        spDarkness.scale.set((rScreenPx * 2) / texW);
        spDarkness.alpha = clamp01(0.6 * dsErase);
        spDarkness.blendMode = PIXI.BLEND_MODES.ERASE;
        if (maskGDarkness) spDarkness.mask = maskGDarkness;
        darknessCont.addChild(spDarkness);
      } else {
        const baseScale = (rScreenPx * 2) / texW;

        const spShadow = new PIXI.Sprite(grad);
        spShadow.anchor.set(0.5);
        spShadow.position.set(center.x, center.y);
        spShadow.scale.set(baseScale * scaleJitter);
        spShadow.alpha = clamp01(baseAlpha * alphaJitter);
        spShadow.blendMode = PIXI.BLEND_MODES.ERASE;
        if (maskGScaled) spShadow.mask = maskGScaled;
        scaledCont.addChild(spShadow);

        const spDarkness = new PIXI.Sprite(grad);
        spDarkness.anchor.set(0.5);
        spDarkness.position.set(center.x, center.y);
        spDarkness.scale.set(baseScale * scaleJitter);
        spDarkness.alpha = clamp01(baseAlpha * alphaJitter);
        spDarkness.blendMode = PIXI.BLEND_MODES.ERASE;
        if (maskGDarkness) spDarkness.mask = maskGDarkness;
        darknessCont.addChild(spDarkness);
      }
    }

    {
      const pulses = glowPulsesRef.current;
      for (let i = 0; i < pulses.length; i++) {
        const p = pulses[i];
        const center = pulseToScreen(map, p);
        if (!center) continue;

        const rImgPx = getGlowRadiusImagePx(p, gridRef.current);
        let rScreenPx = worldToScreenPx(rImgPx);
        if (!isFinite(rScreenPx) || rScreenPx <= 0.5) continue;

        const polyPts =
          p.los === false
            ? null
            : computeVisibilityPolygonByIntersect(
                center,
                segments,
                Math.max(1, rScreenPx),
                192
              );

        let maskGScaled = null;
        let maskGDarkness = null;

        if (polyPts && polyPts.length >= 3) {
          maskGScaled = new PIXI.Graphics();
          maskGScaled.beginFill(0xffffff, 1);
          maskGScaled.moveTo(polyPts[0].x, polyPts[0].y);
          for (let j = 1; j < polyPts.length; j++) maskGScaled.lineTo(polyPts[j].x, polyPts[j].y);
          maskGScaled.closePath();
          maskGScaled.endFill();
          scaledCont.addChild(maskGScaled);

          maskGDarkness = new PIXI.Graphics();
          maskGDarkness.beginFill(0xffffff, 1);
          maskGDarkness.moveTo(polyPts[0].x, polyPts[0].y);
          for (let j = 1; j < polyPts.length; j++) maskGDarkness.lineTo(polyPts[j].x, polyPts[j].y);
          maskGDarkness.closePath();
          maskGDarkness.endFill();
          darknessCont.addChild(maskGDarkness);
        }

        const t = clamp01((nowS - p.t0) / Math.max(0.0001, p.durationS));
        const fade = 1 - t;
        const baseAlpha = clamp01(p.alpha ?? 1) * fade;

        const innerR = Math.min(
          rScreenPx * 0.28,
          oneSquareScreenPx(map, gridRef.current) * 0.6
        );

        if (innerR > 0.5) {
          const hardShadow = new PIXI.Graphics();
          hardShadow.beginFill(0xffffff, 1);
          hardShadow.drawCircle(center.x, center.y, innerR);
          hardShadow.endFill();
          hardShadow.alpha = baseAlpha;
          hardShadow.blendMode = PIXI.BLEND_MODES.ERASE;
          if (maskGScaled) hardShadow.mask = maskGScaled;
          scaledCont.addChild(hardShadow);

          const hardDarkness = new PIXI.Graphics();
          hardDarkness.beginFill(0xffffff, 1);
          hardDarkness.drawCircle(center.x, center.y, innerR);
          hardDarkness.endFill();
          hardDarkness.alpha = baseAlpha;
          hardDarkness.blendMode = PIXI.BLEND_MODES.ERASE;
          if (maskGDarkness) hardDarkness.mask = maskGDarkness;
          darknessCont.addChild(hardDarkness);
        }

        const spShadow = new PIXI.Sprite(grad);
        spShadow.anchor.set(0.5);
        spShadow.position.set(center.x, center.y);
        spShadow.scale.set((rScreenPx * 2) / texW);
        spShadow.alpha = baseAlpha;
        spShadow.blendMode = PIXI.BLEND_MODES.ERASE;
        if (maskGScaled) spShadow.mask = maskGScaled;
        scaledCont.addChild(spShadow);

        const spDarkness = new PIXI.Sprite(grad);
        spDarkness.anchor.set(0.5);
        spDarkness.position.set(center.x, center.y);
        spDarkness.scale.set((rScreenPx * 2) / texW);
        spDarkness.alpha = baseAlpha;
        spDarkness.blendMode = PIXI.BLEND_MODES.ERASE;
        if (maskGDarkness) spDarkness.mask = maskGDarkness;
        darknessCont.addChild(spDarkness);
      }
    }

    renderer.render(root, {
      renderTexture: shadowMinusLightsRTRef.current,
      clear: true,
    });

    const feather = new BlurFilter();
    feather.blur = 8;
    feather.quality = 1;
    feather.resolution = 1;
    feather.padding = 8;
    darknessCont.filters = [feather];

    renderer.render(darknessCont, {
      renderTexture: darknessRTRef.current,
      clear: true,
    });

    try {
      root.destroy({ children: true });
    } catch {}
    try {
      darknessCont.destroy({ children: true });
    } catch {}
  }

  function renderOverlayAssets({ W, H }) {
    const overlaysContainer = overlaysContainerRef.current;
    if (!overlaysContainer) return;

    try {
      const kids = overlaysContainer.removeChildren();
      for (let i = 0; i < kids.length; i++) {
        try {
          kids[i].destroy({ children: true });
        } catch {}
      }
    } catch {}

    const overlayList = Array.isArray(overlayAssetsRef.current)
      ? overlayAssetsRef.current
      : [];

    const overlaysSorted = overlayList.slice().sort((a, b) => {
      const za = Number.isFinite(a?.z_index) ? a.z_index : 0;
      const zb = Number.isFinite(b?.z_index) ? b.z_index : 0;
      return za - zb;
    });

    for (let i = 0; i < overlaysSorted.length; i++) {
      const a = overlaysSorted[i];
      if (!a || !a.url || a.visible === 0) continue;

      const tex = textureFromUrl(a.url, texturesRef);
      if (!tex) continue;

      const wImg = Number(a.placement_width) || 256;
      const hImg = Number(a.placement_height) || 256;
      const cxImg = Number.isFinite(a.placement_x)
        ? a.placement_x
        : (imageSizeRef.current?.[0] || 0) / 2;
      const cyImg = Number.isFinite(a.placement_y)
        ? a.placement_y
        : (imageSizeRef.current?.[1] || 0) / 2;

      const c = map.latLngToContainerPoint([cyImg, cxImg]);
      const pW0 = map.latLngToContainerPoint([cyImg, cxImg - wImg / 2]);
      const pW1 = map.latLngToContainerPoint([cyImg, cxImg + wImg / 2]);
      const pH0 = map.latLngToContainerPoint([cyImg - hImg / 2, cxImg]);
      const pH1 = map.latLngToContainerPoint([cyImg + hImg / 2, cxImg]);

      const screenW = Math.max(1, Math.hypot(pW1.x - pW0.x, pW1.y - pW0.y));
      const screenH = Math.max(1, Math.hypot(pH1.x - pH0.x, pH1.y - pH0.y));

      const sp = new PIXI.Sprite(tex);
      sp.anchor.set(0.5, 0.5);
      sp.position.set(c.x, c.y);
      sp.width = screenW;
      sp.height = screenH;
      sp.rotation = ((Number(a.rotation_deg) || 0) * Math.PI) / 180;
      sp.alpha = Number.isFinite(a.opacity) ? clamp01(a.opacity) : 1;

      const k = clamp01(1 - clamp01(overlayDarkenRef.current));
      const g = Math.max(0, Math.min(255, Math.round(255 * k)));
      sp.tint = (g << 16) | (g << 8) | g;

      overlaysContainer.addChild(sp);
    }
  }

  return (
    <div
      ref={holderRef}
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        zIndex,
        background: "transparent",
      }}
    />
  );
}

class MaskAlphaMultiplyFilter extends PIXI.Filter {
  constructor(maskTex) {
    const frag = `
precision mediump float;
varying vec2 vTextureCoord;
uniform sampler2D uSampler;
uniform sampler2D uMaskTex;
void main() {
  vec4 c = texture2D(uSampler, vTextureCoord);
  float m = texture2D(uMaskTex, vTextureCoord).a;
  gl_FragColor = vec4(c.rgb, c.a * m);
}
`;
    super(undefined, frag, {
      uMaskTex: maskTex || PIXI.Texture.WHITE,
    });
  }
}

class UnrevealedFogCombineFilter extends PIXI.Filter {
  constructor(hardMaskTex, softMaskTex) {
    const frag = `
precision mediump float;
varying vec2 vTextureCoord;
uniform sampler2D uHardMask;
uniform sampler2D uSoftMask;
void main() {
  float softA = texture2D(uSoftMask, vTextureCoord).a;
  float visibleA = clamp(softA, 0.0, 1.0);
  float unrevealedA = 1.0 - visibleA;
  gl_FragColor = vec4(1.0, 1.0, 1.0, unrevealedA);
}
`;
    super(undefined, frag, {
      uHardMask: hardMaskTex || PIXI.Texture.WHITE,
      uSoftMask: softMaskTex || PIXI.Texture.WHITE,
    });
  }
}

function destroyRT(ref) {
  try {
    ref.current?.destroy(true);
  } catch {}
  ref.current = null;
}

function clamp01(v) {
  return Math.max(0, Math.min(1, Number(v) || 0));
}

function clamp(v, min, max) {
  return Math.max(min, Math.min(max, Number(v) || 0));
}

function imageToScreenPoint(map, x, y) {
  const pt = map.latLngToContainerPoint([y, x]);
  return { x: pt.x, y: pt.y };
}

function imagePxToScreenPx(map, imagePx) {
  const p0 = map.latLngToContainerPoint([0, 0]);
  const pX = map.latLngToContainerPoint([0, imagePx]);
  const pY = map.latLngToContainerPoint([imagePx, 0]);
  const sx = Math.hypot(pX.x - p0.x, pX.y - p0.y);
  const sy = Math.hypot(pY.x - p0.x, pY.y - p0.y);
  return (sx + sy) / 2;
}

function makeWhiteRadialTexture(baseRadiusPx = 512) {
  const size = baseRadiusPx * 2;
  const cvs = document.createElement("canvas");
  cvs.width = size;
  cvs.height = size;
  const ctx = cvs.getContext("2d");
  const cx = baseRadiusPx;
  const cy = baseRadiusPx;

  const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, baseRadiusPx);
  g.addColorStop(0.0, "rgba(255,255,255,1.0)");
  g.addColorStop(0.2, "rgba(255,255,255,1.0)");
  g.addColorStop(0.5, "rgba(255,255,255,0.35)");
  g.addColorStop(1.0, "rgba(255,255,255,0.0)");

  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(cx, cy, baseRadiusPx, 0, Math.PI * 2);
  ctx.fill();

  return PIXI.Texture.from(cvs);
}

function mergeLights(db = [], propsArr = []) {
  const byKey = new Map();

  const add = (m) => {
    const k =
      m && m.id != null
        ? `id:${m.id}`
        : `xy:${Math.round(m?.x ?? 0)}:${Math.round(m?.y ?? 0)}:${Math.round(m?.reach ?? 0)}`;

    if (!byKey.has(k)) {
      byKey.set(k, m);
    } else {
      const prev = byKey.get(k);
      byKey.set(k, {
        ...prev,
        ...m,
        flicker: Math.max(Number(prev?.flicker || 0), Number(m?.flicker || 0)),
        intensity: m?.intensity != null ? m.intensity : prev?.intensity,
        reach: m?.reach != null ? m.reach : prev?.reach,
        reach_units: m?.reach_units || prev?.reach_units || null,
        type: prev?.type || m?.type,
        light_type: prev?.light_type || m?.light_type,
      });
    }
  };

  (Array.isArray(propsArr) ? propsArr : []).forEach(add);
  (Array.isArray(db) ? db : []).forEach(add);

  return Array.from(byKey.values());
}

function markerToScreen(map, m) {
  if (!map || !m) return null;
  if (m.__screen) return { x: m.__xScreen, y: m.__yScreen };
  if (m.lat !== undefined && m.lng !== undefined) {
    const p = map.latLngToContainerPoint([m.lat, m.lng]);
    return { x: p.x, y: p.y };
  }
  if (m.x !== undefined && m.y !== undefined) {
    return imageToScreenPoint(map, m.x, m.y);
  }
  return null;
}

function pulseToScreen(map, p) {
  if (!map || !p) return null;
  if (p.lat !== undefined && p.lng !== undefined) {
    const pt = map.latLngToContainerPoint([p.lat, p.lng]);
    return { x: pt.x, y: pt.y };
  }
  if (p.x !== undefined && p.y !== undefined) {
    return imageToScreenPoint(map, p.x, p.y);
  }
  return null;
}

function buildSegmentsFromPolys(polys) {
  const out = [];
  for (let p = 0; p < polys.length; p++) {
    const poly = polys[p];
    if (!poly || poly.length < 2) continue;
    for (let i = 0; i < poly.length - 1; i++) {
      out.push([poly[i], poly[i + 1]]);
    }
    const first = poly[0];
    const last = poly[poly.length - 1];
    if (!first || !last) continue;
    if (first.x !== last.x || first.y !== last.y) {
      out.push([last, first]);
    }
  }
  return out;
}

function computeVisibilityPolygonByIntersect(origin, segments, maxDistance = 2500, rays = 256) {
  const pts = [];
  const TAU = Math.PI * 2;
  for (let i = 0; i < rays; i++) {
    const ang = (TAU * i) / rays;
    const d = { x: Math.cos(ang), y: Math.sin(ang) };
    let tMin = maxDistance;
    for (let s = 0; s < segments.length; s++) {
      const a = segments[s][0];
      const b = segments[s][1];
      const t = raySegmentT(origin, d, a, b);
      if (t !== null && t >= 0 && t < tMin) tMin = t;
    }
    const tHit = Math.min(tMin, maxDistance);
    pts.push({ x: origin.x + d.x * tHit, y: origin.y + d.y * tHit });
  }
  return pts;
}

function raySegmentT(p, d, a, b) {
  const v = { x: b.x - a.x, y: b.y - a.y };
  const denom = cross2(d, v);
  if (Math.abs(denom) < 1e-8) return null;
  const ap = { x: a.x - p.x, y: a.y - p.y };
  const t = cross2(ap, v) / denom;
  const u = cross2(ap, d) / denom;
  if (u < 0 || u > 1) return null;
  if (t < 0) return null;
  return t;
}

function cross2(a, b) {
  return a.x * b.y - a.y * b.x;
}

function azimuthToShadowVector(azimuthDeg) {
  const theta = (Number(azimuthDeg || 0) * Math.PI) / 180;
  return normalizeVec({
    x: Math.sin(theta),
    y: Math.cos(theta),
  });
}

function normalizeVec(v) {
  const len = Math.hypot(v?.x || 0, v?.y || 0);
  if (!len) return { x: 0, y: 1 };
  return { x: v.x / len, y: v.y / len };
}

function polygonSignedArea(points) {
  if (!Array.isArray(points) || points.length < 3) return 0;
  let area2 = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    area2 += a.x * b.y - b.x * a.y;
  }
  return area2 * 0.5;
}

function outwardNormalForEdge(a, b, clockwise) {
  const ex = b.x - a.x;
  const ey = b.y - a.y;
  const n = clockwise ? { x: ey, y: -ex } : { x: -ey, y: ex };
  return normalizeVec(n);
}

function buildShadowCasterSegmentsFromScreenPolys(screenPolys, shadowDir) {
  const out = [];
  const dir = normalizeVec(shadowDir);

  for (let p = 0; p < screenPolys.length; p++) {
    const poly = screenPolys[p];
    if (!Array.isArray(poly) || poly.length < 2) continue;

    const clockwise = polygonSignedArea(poly) < 0;

    for (let i = 0; i < poly.length; i++) {
      const a = poly[i];
      const b = poly[(i + 1) % poly.length];
      const edgeLen = Math.hypot(b.x - a.x, b.y - a.y);
      if (edgeLen < 0.001) continue;

      const outward = outwardNormalForEdge(a, b, clockwise);
      const facing = outward.x * dir.x + outward.y * dir.y;
      if (facing <= 0.01) continue;

      out.push({ a, b, outward, facing });
    }
  }

  return out;
}

function hexToRgbInt(hex) {
  const safe = String(hex || "").trim().replace("#", "");
  if (!/^[0-9a-f]{6}$/i.test(safe)) return { r: 0, g: 0, b: 0 };
  return {
    r: parseInt(safe.slice(0, 2), 16),
    g: parseInt(safe.slice(2, 4), 16),
    b: parseInt(safe.slice(4, 6), 16),
  };
}

function rgbIntToHexInt(rgb) {
  const r = clamp(Math.round(rgb?.r ?? 0), 0, 255);
  const g = clamp(Math.round(rgb?.g ?? 0), 0, 255);
  const b = clamp(Math.round(rgb?.b ?? 0), 0, 255);
  return (r << 16) + (g << 8) + b;
}

function inferMapIdFromPath() {
  try {
    const path = window?.location?.pathname || "";
    const m = path.match(/(\d+)(?!.*\d)/);
    return m ? parseInt(m[1], 10) : null;
  } catch {
    return null;
  }
}

function parseDeg(v) {
  if (v == null) return null;
  let n = v;
  if (typeof v === "string") {
    const m = v.match(/-?\d+(\.\d+)?/);
    n = m ? parseFloat(m[0]) : NaN;
  }
  if (typeof n !== "number" || !isFinite(n)) return null;
  return clamp(n, 0, 90);
}

function isRenderableLight(m) {
  if (!m) return false;
  const hasXY = m.x !== undefined && m.y !== undefined;
  const hasLatLng = m.lat !== undefined && m.lng !== undefined;
  return (hasXY || hasLatLng) && m.reach != null;
}

function lightSeed(m) {
  let s = 0;
  if (m.id != null) s += Number(m.id) * 0.1337 || 0;
  if (m.x != null) s += m.x * 0.0173;
  if (m.y != null) s += m.y * 0.0199;
  if (m.lat != null) s += m.lat * 0.0179;
  if (m.lng != null) s += m.lng * 0.0217;
  return s;
}

const NO_FLICKER = Object.freeze({ brightness: 0, radius: 0 });

function hasEnabledFlicker(...lightLists) {
  for (let listIndex = 0; listIndex < lightLists.length; listIndex++) {
    const list = lightLists[listIndex];
    if (!Array.isArray(list)) continue;
    for (let i = 0; i < list.length; i++) {
      if (Number(list[i]?.flicker || 0) > 0) return true;
    }
  }
  return false;
}

function candleFlicker(t, seed) {
  // Each noise band eases between short-lived random targets. There are no
  // instantaneous value changes: every transition is smoothly interpolated.
  const fast = smoothNoise1D(t, 0.16, seed + 11.7);
  const medium = smoothNoise1D(t, 0.38, seed + 37.1);
  const slow = smoothNoise1D(t, 1.15, seed + 83.9);

  // Brightness carries most of the flame movement. Radius changes remain
  // deliberately restrained so the illuminated area does not pump in/out.
  const brightness = fast * 0.5 + medium * 0.32 + slow * 0.18;
  const radius = medium * 0.38 + slow * 0.62;

  return {
    brightness: clampSigned(brightness),
    radius: clampSigned(radius),
  };
}

function smoothNoise1D(t, periodSeconds, seed) {
  const position = t / Math.max(0.001, periodSeconds);
  const index = Math.floor(position);
  const fraction = position - index;

  // Quintic easing gives zero velocity and acceleration at both ends, so a
  // new target never produces a visible corner or snap in the animation.
  const eased = fraction * fraction * fraction *
    (fraction * (fraction * 6 - 15) + 10);

  const a = seededSignedValue(index, seed);
  const b = seededSignedValue(index + 1, seed);
  return a + (b - a) * eased;
}

function seededSignedValue(index, seed) {
  const x = Math.sin((index + seed * 17.173) * 12.9898) * 43758.5453;
  return (x - Math.floor(x)) * 2 - 1;
}

function clampSigned(value) {
  return Math.max(-1, Math.min(1, value));
}

function isGridUnits(u) {
  return u === "squares" || u === "cells" || u === "grid";
}

function lightImageRadiusPx(lm, grid) {
  const units = (lm.reach_units || "").toLowerCase();
  const reachNum = typeof lm.reach === "number" ? lm.reach : parseFloat(lm.reach) || 0;
  const mpp = grid ? grid.metre_in_pixels ?? grid.meter_in_pixels : null;
  const squareLen = grid ? grid.square_length : null;

  if ((units === "metres" || units === "meters") && typeof mpp === "number" && isFinite(mpp) && mpp > 0) {
    return reachNum * mpp;
  }
  if (isGridUnits(units) && typeof squareLen === "number" && isFinite(squareLen) && squareLen > 0) {
    return reachNum * squareLen;
  }
  if (units === "px" || units === "pixel" || units === "pixels") {
    return reachNum;
  }
  if (typeof mpp === "number" && isFinite(mpp) && mpp > 0) {
    return reachNum * mpp;
  }
  if (typeof squareLen === "number" && isFinite(squareLen) && squareLen > 0) {
    return reachNum * squareLen;
  }
  return reachNum > 0 ? reachNum : 100;
}

function isDarkSight(lm) {
  const t = ((lm.light_type || lm.type || "") + "").toLowerCase();
  if (t === "vision" || t === "darksight" || t === "darkvision") return true;
  if (lm.is_vision || lm.darksight || lm.darkvision) return true;
  return false;
}

function oneSquareScreenPx(map, grid) {
  const sq = grid ? grid.square_length : null;
  if (typeof sq === "number" && isFinite(sq) && sq > 0) {
    return imagePxToScreenPx(map, sq);
  }
  const mpp = grid ? grid.metre_in_pixels ?? grid.meter_in_pixels : null;
  if (typeof mpp === "number" && isFinite(mpp) && mpp > 0) {
    return imagePxToScreenPx(map, mpp);
  }
  return 50;
}

function normalizeGlowEventPayload(d, grid) {
  if (!d) return null;

  const hasXY = typeof d.x === "number" && typeof d.y === "number";
  const hasLatLng = typeof d.lat === "number" && typeof d.lng === "number";
  if (!hasXY && !hasLatLng) return null;

  const durationS =
    typeof d.durationS === "number" && d.durationS > 0
      ? d.durationS
      : typeof d.durationMs === "number" && d.durationMs > 0
        ? d.durationMs / 1000
        : 0.75;

  const radiusM =
    typeof d.radiusM === "number" && d.radiusM > 0
      ? d.radiusM
      : typeof d.radius_m === "number" && d.radius_m > 0
        ? d.radius_m
        : null;

  const radiusPx =
    typeof d.radiusPx === "number" && d.radiusPx > 0
      ? d.radiusPx
      : null;

  const alpha = typeof d.alpha === "number" ? clamp01(d.alpha) : 1;
  const los = d.los === false ? false : true;

  return {
    ...(hasXY ? { x: d.x, y: d.y } : { lat: d.lat, lng: d.lng }),
    radiusM,
    radiusPx,
    durationS,
    alpha,
    los,
    t0: performance.now() / 1000,
  };
}

function getGlowRadiusImagePx(p, grid) {
  if (!p) return 0;
  if (typeof p.radiusPx === "number" && isFinite(p.radiusPx) && p.radiusPx > 0) {
    return p.radiusPx;
  }

  const mpp = grid ? grid.metre_in_pixels ?? grid.meter_in_pixels : null;
  if (typeof p.radiusM === "number" && isFinite(p.radiusM) && p.radiusM > 0) {
    if (typeof mpp === "number" && isFinite(mpp) && mpp > 0) {
      return p.radiusM * mpp;
    }
    return p.radiusM;
  }

  return 0;
}

function getRevealRadiusImagePx(grid, revealRadiusMetres) {
  const mpp = grid ? grid.metre_in_pixels ?? grid.meter_in_pixels : null;
  if (typeof mpp === "number" && isFinite(mpp) && mpp > 0) {
    return (Number(revealRadiusMetres) || 0) * mpp;
  }
  return 100;
}

function makeGaussianBlurFilters(sigmaPx, width, height) {
  const R = 12;
  const kernel = new Float32Array(R + 1);
  const s2 = Math.max(0.25, sigmaPx * sigmaPx);
  for (let i = 0; i <= R; i++) kernel[i] = Math.exp(-(i * i) / (2 * s2));
  let sum = kernel[0];
  for (let i = 1; i <= R; i++) sum += 2 * kernel[i];
  for (let i = 0; i <= R; i++) kernel[i] /= sum;

  const frag = `
precision mediump float;
varying vec2 vTextureCoord;
uniform sampler2D uSampler;
uniform vec2 uDirection;
uniform vec2 uTexelSize;
uniform float uKernel[${R + 1}];
void main() {
  vec4 c = texture2D(uSampler, vTextureCoord) * uKernel[0];
  ${Array.from({ length: R }, (_, i) => {
    const j = i + 1;
    return `
      c += texture2D(uSampler, vTextureCoord + uDirection * uTexelSize * ${j}.0) * uKernel[${j}];
      c += texture2D(uSampler, vTextureCoord - uDirection * uTexelSize * ${j}.0) * uKernel[${j}];
    `;
  }).join("\n")}
  gl_FragColor = c;
}
`;

  const uniformsH = {
    uDirection: new Float32Array([1, 0]),
    uTexelSize: new Float32Array([
      1 / Math.max(1, width),
      1 / Math.max(1, height),
    ]),
    uKernel: kernel,
  };

  const uniformsV = {
    uDirection: new Float32Array([0, 1]),
    uTexelSize: new Float32Array([
      1 / Math.max(1, width),
      1 / Math.max(1, height),
    ]),
    uKernel: kernel,
  };

  const h = new PIXI.Filter(undefined, frag, uniformsH);
  const v = new PIXI.Filter(undefined, frag, uniformsV);
  return [h, v];
}

function textureFromUrl(u, cacheRef) {
  if (!u) return null;

  let url = String(u || "");
  if (!(url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:"))) {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    url = origin + url;
  }

  const cache = cacheRef.current || {};
  if (cache[url]) return cache[url];

  const tex = PIXI.Texture.from(url);
  cache[url] = tex;
  cacheRef.current = cache;
  return tex;
}

function getMapTransformKey(map) {
  try {
    const center = map.getCenter();
    const zoom = map.getZoom();
    const size = map.getSize();
    return [
      Math.round((center?.lat || 0) * 1000),
      Math.round((center?.lng || 0) * 1000),
      Math.round((zoom || 0) * 1000),
      size?.x || 0,
      size?.y || 0,
    ].join("|");
  } catch {
    return "0|0|0|0|0";
  }
}